'use client';

import React from 'react';
import Link from 'next/link';
import { E } from '@/lib/editorial';

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
  errorInfo?: React.ErrorInfo;
}

interface ErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: React.ComponentType<{ error?: Error; retry: () => void }>;
}

const btnBase: React.CSSProperties = {
  borderRadius: 0,
  textDecoration: 'none',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
};

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    const isHydrationError =
      error.message?.includes('Hydration') ||
      error.message?.includes('hydration') ||
      error.message?.includes('Text content does not match') ||
      error.message?.includes('did not match');

    return {
      hasError: !isHydrationError,
      error: isHydrationError ? undefined : error,
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    const isHydrationError =
      error.message?.includes('Hydration') ||
      error.message?.includes('hydration') ||
      error.message?.includes('Text content does not match') ||
      error.message?.includes('did not match');

    if (isHydrationError) {
      console.warn('Hydration mismatch detected (possibly from browser translation):', error.message);
      return;
    }

    console.error('ErrorBoundary caught an error:', error, errorInfo);

    if (process.env.NODE_ENV === 'production') {
      console.error('Production error:', {
        error: error.message,
        stack: error.stack,
        componentStack: errorInfo.componentStack,
        timestamp: new Date().toISOString(),
      });
    }

    this.setState({
      error,
      errorInfo,
    });
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: undefined, errorInfo: undefined });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        const FallbackComponent = this.props.fallback;
        return <FallbackComponent error={this.state.error} retry={this.handleRetry} />;
      }

      const err = this.state.error;

      return (
        <div style={{ ...E.page, padding: 0 }}>
          <div className="e-pad" style={{ maxWidth: 720, margin: '0 auto', paddingTop: 56, paddingBottom: 80 }}>
            <div style={E.label}>Error · Application</div>

            <p
              style={{
                fontFamily: E.serif,
                fontSize: 'clamp(72px, 18vw, 120px)',
                fontWeight: 400,
                fontStyle: 'italic',
                lineHeight: 0.9,
                margin: '0 0 8px',
                color: E.goldDeep,
                opacity: 0.45,
                letterSpacing: '-0.02em',
              }}
            >
              !
            </p>

            <h1 style={{ ...E.h1, fontSize: 'clamp(36px, 5vw, 52px)', marginBottom: 16 }}>
              Something went wrong.
            </h1>

            <p style={{ ...E.body, color: E.muted, maxWidth: 520, marginBottom: 28 }}>
              We encountered an unexpected error. This might be due to a temporary issue during deployment.
              Try again, or return home and come back in a moment.
            </p>

            {process.env.NODE_ENV === 'development' && err && (
              <details
                style={{
                  ...E.body,
                  fontSize: 13,
                  color: E.inkSoft,
                  marginBottom: 28,
                  maxWidth: 520,
                }}
              >
                <summary style={{ cursor: 'pointer', color: E.goldDeep, ...E.cap, fontSize: 10 }}>
                  Error details (development only)
                </summary>
                <pre
                  style={{
                    marginTop: 12,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    padding: 16,
                    background: E.cream2,
                    border: `1px solid ${E.ruleSoft}`,
                    fontSize: 11,
                    lineHeight: 1.5,
                    fontFamily: 'ui-monospace, monospace',
                    color: E.ink,
                  }}
                >
                  {err.message}
                  {err.stack ? `\n\n${err.stack}` : ''}
                  {this.state.errorInfo?.componentStack
                    ? `\n\nComponent stack:${this.state.errorInfo.componentStack}`
                    : ''}
                </pre>
              </details>
            )}

            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 12,
                paddingTop: 8,
                borderTop: `1px solid ${E.rule}`,
              }}
            >
              <button type="button" onClick={this.handleRetry} style={{ ...E.btn, ...btnBase }}>
                Try again
              </button>
              <Link href="/" style={{ ...E.btnGhost, ...btnBase }}>
                Return home
              </Link>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export function useErrorHandler() {
  return (error: Error, errorInfo?: { componentStack?: string }) => {
    console.error('Error caught by useErrorHandler:', error, errorInfo);
    throw error;
  };
}
